import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLocalStorage } from '../hook/useLocalStorage'; // Adjust the import path as needed
import { api } from '../services';
import { useForm } from 'react-hook-form';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { PersianKeyboardModal } from '../components/PersianKeyboardModal';
import { isPersianText } from '../utils/persianKeyboardDetector';

export const Login = () => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const ov = searchParams.get('ov'); // read ov from query (e.g. ?ov=1)
  const sortOrder = localStorage.getItem('selectedSortOrder');
  const [showPersianModal, setShowPersianModal] = useState(false);
  const [isKeyboardPersian, setIsKeyboardPersian] = useState(true); // New state to track keyboard language

  // Add console.log to show sortOrder
  console.log('Selected SortOrder:', sortOrder);

  // Watch phone number for debugging
  const phoneNumber = watch('phoneNumber');
  console.log('Current phone number:', phoneNumber);

  // Helper function to convert Persian/Arabic numbers to English
  const convertToEnglishNumbers = (value) => {
    const persianToEnglish = {
      '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
      '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
      '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
      '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
    };
    
    let convertedValue = value.toString().trim().replace(/[\s\-\(\)]/g, '');
    
    for (let persian in persianToEnglish) {
      convertedValue = convertedValue.replace(new RegExp(persian, 'g'), persianToEnglish[persian]);
    }
    
    return convertedValue;
  };

  // Custom validation function for phone number
  const validatePhoneNumber = (value) => {
    if (!value) {
      return 'شماره موبایل الزامی است';
    }
    
    // Remove any whitespace, dashes, and parentheses
    const cleanValue = value.toString().trim().replace(/[\s\-\(\)]/g, '');
    
    console.log('Original value:', value);
    console.log('Clean value:', cleanValue);
    
    // Convert Persian and Arabic numbers to English for processing
    const persianToEnglish = {
      '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
      '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
      '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
      '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
    };
    
    let convertedValue = cleanValue;
    for (let persian in persianToEnglish) {
      convertedValue = convertedValue.replace(new RegExp(persian, 'g'), persianToEnglish[persian]);
    }
    
    console.log('Converted value:', convertedValue);
    
    // Handle +98 country code - remove it and treat as if it starts with 0
    if (convertedValue.startsWith('+98')) {
      convertedValue = convertedValue.substring(3); // Remove +98
      console.log('Removed +98 country code:', convertedValue);
    }
    
    // Check if the converted value contains only digits
    if (!/^\d+$/.test(convertedValue)) {
      return 'شماره موبایل باید فقط شامل اعداد باشد';
    }
    
    // Auto-add leading zero if missing and length is 10 (valid Iranian mobile without leading zero)
    if (!convertedValue.startsWith('0') && convertedValue.length === 10) {
      convertedValue = '0' + convertedValue;
      console.log('Auto-added leading zero:', convertedValue);
    }
    
    // Check if the converted value starts with 0
    if (!convertedValue.startsWith('0')) {
      return 'شماره موبایل باید با صفر شروع شود';
    }
    
    // Check the length
    if (convertedValue.length !== 11) {
      return `شماره موبایل باید ۱۱ رقم باشد (فعلاً ${convertedValue.length} رقم است)`;
    }
    
    // Store the converted value for later use in the form
    return true;
  };

  // Function to detect keyboard language
  const detectKeyboardLanguage = (e) => {
    const key = e.key;
    if (key && key.length === 1) { // Only check for letter keys
      // Only detect English letters (a-z, A-Z), not digits
      const isEnglishLetter = /[a-zA-Z]/.test(key);
      if (isEnglishLetter) {
        setIsKeyboardPersian(false);
        setShowPersianModal(true); // Show modal if non-Persian key is pressed
        e.preventDefault(); // Prevent typing non-Persian characters
        return false;
      }
    }
    // Don't reset keyboard state for every key press, only for letters
    return true;
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    
    // Allow Persian digits (۰-۹), Arabic digits (٠-٩), English digits (0-9), spaces,
    // common phone number separators like dash and parentheses, and + for country code
    const allowedPattern = /^[۰-۹٠-٩0-9\s\-\(\)+]*$/;
    
    if (value && !allowedPattern.test(value)) {
      setShowPersianModal(true);
      // Clear the input to prevent non-numeric text
      e.target.value = '';
    }
  };

  const onSubmit = async (data) => {
    try {
      let { phoneNumber } = data;
      
      // Convert Persian and Arabic numbers to English before sending
      const persianToEnglish = {
        '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
        '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
        '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
        '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
      };
      
      // Remove any whitespace, dashes, and parentheses
      phoneNumber = phoneNumber.toString().trim().replace(/[\s\-\(\)]/g, '');
      
      // Convert Persian/Arabic numbers to English
      for (let persian in persianToEnglish) {
        phoneNumber = phoneNumber.replace(new RegExp(persian, 'g'), persianToEnglish[persian]);
      }
      
      // Handle +98 country code - remove it and treat as if it starts with 0
      if (phoneNumber.startsWith('+98')) {
        phoneNumber = phoneNumber.substring(3); // Remove +98
        console.log('Removed +98 country code in submission:', phoneNumber);
      }
      
      // Auto-add leading zero if missing and length is 10 (valid Iranian mobile without leading zero)
      if (!phoneNumber.startsWith('0') && phoneNumber.length === 10) {
        phoneNumber = '0' + phoneNumber;
        console.log('Auto-added leading zero in submission:', phoneNumber);
      }
      
      console.log('Sending OTP to:', phoneNumber);

      const response = await api.get(
        `Home/SendOTP?MobileNumber=${phoneNumber}&OID=1&SortOrder=${sortOrder || ''}`,
      );

      let responseData = response.data;
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (error) {
          console.error('Invalid JSON string:', responseData);
          throw new Error('Failed to parse JSON');
        }
      }

      if (responseData.ErrorMessage) {
        return toast.error(responseData.ErrorMessage);
      }
      if (responseData.SuccessMessage) {
        localStorage.setItem('KEYS', responseData.Key);
        // preserve ov flag if present so LoginVerify can forward to Paymentvo after verification
        const extra = [];
        if (sortOrder) extra.push(`SortOrder=${encodeURIComponent(sortOrder)}`);
        if (ov === '1') extra.push('ov=1');
        const extraQuery = extra.length ? `&${extra.join('&')}` : '';
        navigate(
          `/LoginVerify?phoneNumber=${encodeURIComponent(phoneNumber)}&key=${encodeURIComponent(responseData.Key)}&IsCompeleteProfile=${encodeURIComponent(responseData.IsCompeleteProfile)}${extraQuery}`
        );
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
    }
  };

  return (
    <>
      <section className="container maxw mb-5">
        <div className="row mb-4">
          <div className="col-lg-12 path">
            <ul>
              <li>
                <Link className="text-decoration-none" to="/">
                  صفحه اصلی
                </Link>
                <FontAwesomeIcon icon={faAngleLeft} className="mr-2" />
              </li>
              <li>
              <span>ورود به سایت</span>
              </li>
            </ul>
          </div>
        </div>
        <div >
          <div className="row gap-3 justify-content-center">
            <div className="col-lg-5 card p-3 shadow rounded-3 mx-2">
              <div className="login-body d-flex flex-column align-items-center pt-4">
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="login-form d-flex flex-column align-items-center w-100"
                >
                  <div className="form-group text-center w-75">
                    <p className="mb-3">
                      برای ادامه لطفا شماره موبایل خود را وارد کنید!
                    </p>
                    <input
                      type="tel"
                      name="phoneNumber"
                      id="phoneNumber"
                      className="form-control rounded-input mb-3"
                      placeholder="شماره تلفن همراه"
                      style={{
                        borderRadius: '10px',
                        padding: '10px',
                        fontSize: '16px',
                        textAlign: 'right',
                      }}
                      onChange={handlePhoneNumberChange}
                      onKeyDown={detectKeyboardLanguage} // Add onKeyDown event handler
                      {...register('phoneNumber', {
                        validate: validatePhoneNumber
                      })}
                    />
                    {errors.phoneNumber && (
                      <p className="text-danger mb-3">{errors.phoneNumber.message}</p>
                    )}
                    <button
                      type="submit"
                      name="login"
                      id="login"
                      className="btn btn-primary login-btn w-100"
                    >
                      ادامه
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="col-lg-5 card p-3 shadow rounded-3 mx-2">
              <img
                src={'/Img/login.jpg'}
                className="login-image w-100 h-100"
                alt="login"
                style={{ objectFit: 'cover' }}
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Persian Keyboard Modal */}
      <PersianKeyboardModal 
        isOpen={showPersianModal} 
        onClose={() => setShowPersianModal(false)} 
      />
    </>
  );
};